import { useQueries } from "@tanstack/react-query";
import { getFolders } from "../../../api/file";
import { MailFolder, FolderSearchResult } from "../../../types/filing";

type FolderNode = { folder: MailFolder; path: string[] };

export function useFolderSearch(
  rootFolders: MailFolder[],
  searchTerm: string
): { results: FolderSearchResult[]; isLoading: boolean } {
  const isSearching = searchTerm.trim().length > 0;

  // Build the known node list: roots + every child already in cache from prior renders.
  // useQueries' enabled flag prevents any fetches when not searching.
  const rootNodes: FolderNode[] = rootFolders.map((f) => ({
    folder: f,
    path: [f.displayName],
  }));

  // First pass: query all root children
  const rootChildQueries = useQueries({
    queries: rootNodes
      .filter((n) => n.folder.childFolderCount > 0)
      .map((n) => ({
        queryKey: ["graphFolders", n.folder.id],
        queryFn: () => getFolders(n.folder.id),
        staleTime: 60_000,
        enabled: isSearching,
      })),
  });

  // Collect level-1 nodes from root child results
  const level1Nodes: FolderNode[] = [];
  const rootsWithChildren = rootNodes.filter((n) => n.folder.childFolderCount > 0);
  for (let i = 0; i < rootsWithChildren.length; i++) {
    const children = rootChildQueries[i]?.data ?? [];
    for (const child of children) {
      level1Nodes.push({ folder: child, path: [...rootsWithChildren[i].path, child.displayName] });
    }
  }

  // Second pass: query children of level-1 nodes
  const level1ChildQueries = useQueries({
    queries: level1Nodes
      .filter((n) => n.folder.childFolderCount > 0)
      .map((n) => ({
        queryKey: ["graphFolders", n.folder.id],
        queryFn: () => getFolders(n.folder.id),
        staleTime: 60_000,
        enabled: isSearching,
      })),
  });

  // Collect level-2 nodes
  const level2Nodes: FolderNode[] = [];
  const level1WithChildren = level1Nodes.filter((n) => n.folder.childFolderCount > 0);
  for (let i = 0; i < level1WithChildren.length; i++) {
    const children = level1ChildQueries[i]?.data ?? [];
    for (const child of children) {
      level2Nodes.push({ folder: child, path: [...level1WithChildren[i].path, child.displayName] });
    }
  }

  // Third pass: query children of level-2 nodes
  const level2ChildQueries = useQueries({
    queries: level2Nodes
      .filter((n) => n.folder.childFolderCount > 0)
      .map((n) => ({
        queryKey: ["graphFolders", n.folder.id],
        queryFn: () => getFolders(n.folder.id),
        staleTime: 60_000,
        enabled: isSearching,
      })),
  });

  // Collect level-3 nodes
  const level3Nodes: FolderNode[] = [];
  const level2WithChildren = level2Nodes.filter((n) => n.folder.childFolderCount > 0);
  for (let i = 0; i < level2WithChildren.length; i++) {
    const children = level2ChildQueries[i]?.data ?? [];
    for (const child of children) {
      level3Nodes.push({ folder: child, path: [...level2WithChildren[i].path, child.displayName] });
    }
  }

  // Fourth pass: query children of level-3 nodes
  const level3ChildQueries = useQueries({
    queries: level3Nodes
      .filter((n) => n.folder.childFolderCount > 0)
      .map((n) => ({
        queryKey: ["graphFolders", n.folder.id],
        queryFn: () => getFolders(n.folder.id),
        staleTime: 60_000,
        enabled: isSearching,
      })),
  });

  // Collect level-4 nodes (leaf — we don't fetch deeper)
  const level4Nodes: FolderNode[] = [];
  const level3WithChildren = level3Nodes.filter((n) => n.folder.childFolderCount > 0);
  for (let i = 0; i < level3WithChildren.length; i++) {
    const children = level3ChildQueries[i]?.data ?? [];
    for (const child of children) {
      level4Nodes.push({ folder: child, path: [...level3WithChildren[i].path, child.displayName] });
    }
  }

  const allNodes = [...rootNodes, ...level1Nodes, ...level2Nodes, ...level3Nodes, ...level4Nodes];

  const allQueries = [...rootChildQueries, ...level1ChildQueries, ...level2ChildQueries, ...level3ChildQueries];
  const isLoading = isSearching && allQueries.some((q) => q.isFetching && !q.data);

  if (!isSearching) {
    return { results: [], isLoading: false };
  }

  const needle = searchTerm.toLowerCase().trim();
  const results: FolderSearchResult[] = allNodes
    .filter((n) => n.folder.displayName.toLowerCase().includes(needle))
    .map((n) => ({
      folder: n.folder,
      path: n.path,
      pathString: n.path.join(" > "),
    }));

  return { results, isLoading };
}
